import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, FileText } from 'lucide-react';
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shared/ui';
import { cn } from '@/lib/cn';
import { nativePickerInputClassName, triggerNativePickerOnClick } from '@/components/shared/ui/input';

interface PageFiltersProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (v: string) => void;
  onEndDateChange: (v: string) => void;
  sortCriteria: string;
  onSortCriteriaChange: (v: string) => void;
  onDownloadExcel: () => void;
  onDownloadPdf?: () => void;
  showPdfDownload?: boolean;
}

/** 풀스크린 헤더 우측: 기간·정렬 (엑셀 슬롯 숨김) */
export function PageFilters({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  sortCriteria,
  onSortCriteriaChange,
  onDownloadExcel,
  onDownloadPdf,
  showPdfDownload = false,
}: PageFiltersProps): React.ReactElement {
  const { t } = useTranslation('class');
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2 origin-right">
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1 h-9">
          <input
            type="date"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            onClick={triggerNativePickerOnClick}
            className={cn(
              'relative text-sm font-medium text-gray-700 focus:outline-none bg-transparent',
              nativePickerInputClassName,
            )}
          />
        </div>
        <div className="w-1.5 h-px bg-gray-300" />
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1 h-9">
          <input
            type="date"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            onClick={triggerNativePickerOnClick}
            className={cn(
              'relative text-sm font-medium text-gray-700 focus:outline-none bg-transparent',
              nativePickerInputClassName,
            )}
          />
        </div>
      </div>

      <Select value={sortCriteria} onValueChange={onSortCriteriaChange}>
        <SelectTrigger className="w-[160px] h-9 text-sm font-bold bg-white border-gray-200 origin-right ml-1">
          <SelectValue placeholder={t('simulationResults.criteriaPlaceholder')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="latest">{t('simulationResults.criteriaLatest')}</SelectItem>
          <SelectItem value="highest">{t('simulationResults.criteriaHighest')}</SelectItem>
          <SelectItem value="lowest">{t('simulationResults.criteriaLowest')}</SelectItem>
          <SelectItem value="all">{t('simulationResults.criteriaAll')}</SelectItem>
        </SelectContent>
      </Select>

      <div className="h-6 w-px bg-slate-200 mx-1" />

      <div className="flex items-center gap-1.5 hidden">
        <Button variant="primary" size="sm" onClick={onDownloadExcel} className="gap-1.5 h-8 text-sm">
          <FileSpreadsheet size={13} />
          {t('simulationResults.excelDownload')}
        </Button>
      </div>

      {showPdfDownload && onDownloadPdf && (
        <div className="flex items-center gap-1.5">
          <Button variant="primary" size="sm" onClick={onDownloadPdf} className="gap-1.5 h-8 text-sm">
            <FileText size={13} />
            {t('simulationResults.pdfDownload')}
          </Button>
        </div>
      )}
    </div>
  );
}
